import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import FormSelect from "Common/Custom/FormSelect";

/** Maps booking_status string → Bootstrap text colour class */
const statusColor = (status) => {
    switch (status) {
        case "AVAILABLE": return "text-success";
        case "CLEANING": return "text-warning";
        case "CHECKED-IN":
        case "BOOKED": return "text-danger";
        case "MAINTENANCE":
        case "DIRTY": return "text-secondary";
        default: return "text-muted";
    }
};

/** Rooms that cannot be selected for a new cleaning task */
const UNSELECTABLE_STATUSES = ["CHECKED-IN", "BOOKED", "MAINTENANCE"];

const LIMIT_OPTIONS = [10, 20, 50, 100].map((n) => ({ label: String(n), value: n }));

/**
 * RoomFacilitiesCheckboxGroup
 * Renders a card grid of rooms. Each card is a checkbox.
 * Selected room IDs are stored in values.room_ids (array).
 *
 * Props:
 *  - values          {Object}   - shared form state
 *  - setValues       {Function} - direct state setter
 *  - facilityOptions {Array}    - room list from the API
 *  - handleChange    {Function} - shared onChange (used for limit select)
 *  - errors          {Object}   - form validation errors
 */
const RoomFacilitiesCheckboxGroup = ({
    values,
    setValues,
    facilityOptions = [],
    handleChange,
    errors,
}) => {
    const [selectedRooms, setSelectedRooms] = useState([]);

    // Sync local state when room_ids changes externally (e.g. edit pre-fill)
    useEffect(() => {
        if (Array.isArray(values.room_ids)) {
            setSelectedRooms(values.room_ids);
        }
    }, [values.room_ids]);

    const toggleRoom = (room_id) => {
        const updated = selectedRooms.includes(room_id)
            ? selectedRooms.filter((id) => id !== room_id)
            : [...selectedRooms, room_id];

        setSelectedRooms(updated);
        setValues((prev) => ({ ...prev, room_ids: updated }));
    };

    return (
        <>
            <div className="col-12 mt-4">
                <hr />
            </div>

            {/* Header row: title + limit selector */}
            <div className="col-12 mb-3">
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <h6 className="mb-1">Select Room</h6>
                        <small className="text-muted">
                            Choose one or more rooms to assign for cleaning
                        </small>
                    </div>
                    <div style={{ minWidth: "150px" }}>
                        <FormSelect
                            field={{
                                name: "limit",
                                type: "select",
                                required: false,
                                fieldSize: "w-auto",
                                labelHide: true,
                                placeholder: "Show",
                            }}
                            value={values.limit || 10}
                            handleChange={handleChange}
                            options={LIMIT_OPTIONS}
                        />
                    </div>
                </div>
            </div>

            {/* Room cards */}
            {facilityOptions.length === 0 ? (
                <div className="col-12 text-center text-muted py-4">
                    <i className="bi bi-door-closed me-2" />
                    No rooms found. Try adjusting your filters.
                </div>
            ) : (
                facilityOptions.map((room) => {
                    const isDisabled = UNSELECTABLE_STATUSES.includes(room.booking_status);
                    const isSelected = selectedRooms.includes(room.room_id);

                    return (
                        <div
                            key={room.room_id}
                            className="col-xl-3 col-lg-4 col-md-6 col-sm-12"
                        >
                            <div
                                className={`border rounded-4 p-3 h-100 shadow-sm position-relative
                                    ${isDisabled ? "bg-light opacity-75" : "bg-white"}
                                    ${!isDisabled ? "room-card-hover" : ""}
                                    ${isSelected ? "border-primary" : ""}`}
                                style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                                onClick={() => !isDisabled && toggleRoom(room.room_id)}
                                role="checkbox"
                                aria-checked={isSelected}
                                aria-disabled={isDisabled}
                                tabIndex={isDisabled ? -1 : 0}
                                onKeyDown={(e) => {
                                    if (!isDisabled && (e.key === " " || e.key === "Enter")) {
                                        e.preventDefault();
                                        toggleRoom(room.room_id);
                                    }
                                }}
                            >
                                {/* Checkbox indicator */}
                                <input
                                    type="checkbox"
                                    className="form-check-input position-absolute top-0 end-0 m-3"
                                    disabled={isDisabled}
                                    checked={isSelected}
                                    onChange={() => { }} // controlled via card click
                                    tabIndex={-1}       // card handles keyboard
                                    aria-hidden="true"
                                />

                                <div className="d-flex flex-column gap-1">
                                    <div className="fw-semibold fs-6">Room {room.room_no}</div>
                                    <div className="text-muted small">Type: {room.room_type || "—"}</div>
                                    <div className="text-muted small">Floor: {room.floor_name || "—"}</div>
                                    <div className={`small fw-medium mt-1 ${statusColor(room.booking_status)}`}>
                                        {room.booking_status}
                                    </div>
                                    {isDisabled && (
                                        <div className="small text-danger mt-1">
                                            <i className="bi bi-lock me-1" />
                                            Cannot assign — room is occupied
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })
            )}

            {/* Validation error */}
            {errors?.room_ids && selectedRooms.length === 0 && (
                <div className="col-12 text-danger text-center mt-2 small">
                    {errors.room_ids}
                </div>
            )}
        </>
    );
};

RoomFacilitiesCheckboxGroup.propTypes = {
    values: PropTypes.object.isRequired,
    setValues: PropTypes.func.isRequired,
    facilityOptions: PropTypes.array,
    handleChange: PropTypes.func.isRequired,
    errors: PropTypes.object,
};

export default RoomFacilitiesCheckboxGroup;
